import * as React from 'react';
import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import EnhancedTable from '../components/common/EnhancedTable';
import ExpandableText from '../components/common/ExpandableText';
import '../components/common/EnhancedTable/EnhancedTableFix.css';
import {
  Card,
  Title,
  Text,
  Grid,
  TextInput
} from '@tremor/react';
import { format, parseISO } from 'date-fns';
import { Search, Filter, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import { Meeting } from '../types/meeting';
import MeetingAttachmentsModal from '../components/Meetings/MeetingAttachmentsModal';
import MeetingSortControls, { SortConfig } from '../components/Meetings/MeetingSortControls';
import MeetingFilterPanel, { MeetingFilter } from '../components/Meetings/MeetingFilterPanel';
import { useFilteredAndSortedMeetings } from '../hooks/useFilteredAndSortedMeetings';
import { formatTextWithLinks } from '../utils/formatters';

export default function Meetings() {
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  
  // Sort state
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: 'date',
    direction: 'desc'
  });
  
  // Filter state
  const [filters, setFilters] = useState<MeetingFilter>({
    community: null,
    year: null,
    dateFrom: null,
    dateTo: null
  });

  // State for attachments modal
  const [selectedMeeting, setSelectedMeeting] = useState<Meeting | null>(null);
  const [isAttachmentsModalOpen, setIsAttachmentsModalOpen] = useState(false);

  // Handle sort
  const handleSort = (field: string) => {
    setSortConfig(prevConfig => ({
      field: field as any,
      direction: 
        prevConfig.field === field && prevConfig.direction === 'asc'
          ? 'desc'
          : 'asc'
    }));
  };

  // Handle filter changes
  const handleFilterChange = (newFilters: Partial<MeetingFilter>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  // Fetch meetings data
  const fetchMeetings = React.useCallback(async () => {
    try {
      const { data: meetingsData, error: meetingsError } = await supabase
        .from('comm_meetings')
        .select(`
          meet_comm,
          meet_date,
          meet_minutes,
          comm_meeting_attachments (
            id,
            title,
            url,
            attachment_type,
            created_at,
            meet_comm,
            meet_date
          )
        `)
        .order('meet_date', { ascending: false });

      if (meetingsError) {
        console.error('Error fetching meetings:', meetingsError);
        throw meetingsError;
      }

      console.log('Meetings data:', meetingsData);
      setMeetings(meetingsData || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    fetchMeetings();
  }, [fetchMeetings]);

  // Get filtered and sorted meetings
  const filteredMeetings = useFilteredAndSortedMeetings(
    meetings,
    filters,
    sortConfig,
    searchQuery
  );

  // Available communities and years for filters
  const communities = React.useMemo(() => {
    return Array.from(new Set(meetings.map(m => m.meet_comm))).sort();
  }, [meetings]);

  const years = React.useMemo(() => {
    const uniqueYears = new Set(meetings.map(m => m.meet_date.substring(0, 4)));
    return Array.from(uniqueYears).sort().reverse();
  }, [meetings]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-800"></div>
      </div>
    );
  }

  return (
    <div className="p-4 h-[calc(100vh-6rem)] overflow-y-auto">
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900">Community Meetings</h1>
          <div className="relative">
            <MeetingFilterPanel
              isOpen={isFilterOpen}
              onClose={() => setIsFilterOpen(false)}
              filters={filters}
              onFilterChange={handleFilterChange}
              communities={communities}
              years={years}
            />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="relative w-full sm:w-64">
            <TextInput
              placeholder="Search meetings..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
            <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
          </div>
          <button
            onClick={() => setIsFilterOpen(!isFilterOpen)}
            className="px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-1"
          >
            <Filter className="h-4 w-4" />
            <span>Filter</span>
          </button>
          <div className="text-base font-bold text-gray-700">
            Total Meetings: {filteredMeetings.length}
          </div>
        </div>

        <Grid numItems={1} numItemsSm={2} numItemsLg={2} className="gap-6">
          <Card>
            <Title>Total Meetings</Title>
            <Text className="mt-2 text-4xl font-bold">{filteredMeetings.length}</Text>
          </Card>
          <Card>
            <Title>Communities with Meetings</Title>
            <Text className="mt-2 text-4xl font-bold">
              {new Set(filteredMeetings.map(m => m.meet_comm)).size}
            </Text>
          </Card>
        </Grid>
      </div>
      
      <div className="mt-4">
        <EnhancedTable
          columns={[
            { 
              id: 'date', 
              header: (
                <div 
                  className="cursor-pointer flex items-center gap-1"
                  onClick={() => handleSort('date')}
                >
                  Date
                  {sortConfig.field === 'date' ? (
                    sortConfig.direction === 'asc' ? 
                      <ArrowUp className="h-4 w-4" /> : 
                      <ArrowDown className="h-4 w-4" />
                  ) : (
                    <ArrowUpDown className="h-4 w-4" />
                  )}
                </div>
              ),
              accessor: (row) => {
                const meetingDate = parseISO(row.meet_date);
                return format(meetingDate, 'PPP');
              },
              width: 'narrow'
            },
            { 
              id: 'community', 
              header: (
                <div 
                  className="cursor-pointer flex items-center gap-1"
                  onClick={() => handleSort('community')}
                >
                  Community
                  {sortConfig.field === 'community' ? (
                    sortConfig.direction === 'asc' ? 
                      <ArrowUp className="h-4 w-4" /> : 
                      <ArrowDown className="h-4 w-4" />
                  ) : (
                    <ArrowUpDown className="h-4 w-4" />
                  )}
                </div>
              ),
              accessor: (row) => (
                <div className="truncate" title={row.meet_comm || 'Not specified'}>
                  {formatTextWithLinks(row.meet_comm || 'Not specified')}
                </div>
              ),
              width: 'medium'
            },
            { 
              id: 'notes', 
              header: (
                <div className="flex items-center justify-between">
                  <span>Notes and Docs</span>
                </div>
              ),
              accessor: (row) => (
                <ExpandableText 
                  text={row.meet_minutes || 'No notes available'} 
                  maxLength={100} 
                  className="text-sm"
                />
              ),
              width: 'wide'
            }
          ]}
          data={filteredMeetings}
          keyField="meet_date"
          width="2800px"
          maxHeight="calc(100vh - 24rem)"
          emptyMessage="No meetings found"
          horizontalScroll={true}
        />
      </div>

      {selectedMeeting && (
        <MeetingAttachmentsModal
          isOpen={isAttachmentsModalOpen}
          onClose={() => {
            setIsAttachmentsModalOpen(false);
            setSelectedMeeting(null);
          }}
          meetComm={selectedMeeting.meet_comm}
          meetDate={selectedMeeting.meet_date}
          attachments={selectedMeeting.comm_meeting_attachments || []}
          onAttachmentsChange={fetchMeetings}
        />
      )}
    </div>
  );
}
